import axios from 'smg/axios';
import * as blockchyp from 'smg/blockchyp';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import * as html from 'smg/html';
import * as models from 'smg/customer-api/models';
import * as template from 'smg/template';
import * as toast from 'smg/toast';

import { CardTokenFields, TokenizedToFields } from '../../blockchyp-card';

import { State } from '../state';
import { Payment } from '../types';
import { PaymentOptionTypeIDtoName } from '../util';

import { BillingAddressForm } from './billing-address';
import { ClearPlusCardFieldInvalid, CreatePaymentOption, DeletePaymentOption, MarkPlusCardFieldInvalid, NewPaymentOption, PendingPaymentOptionID } from './options';

import { MountCreditCard, RefillCreditFields, RenderCreditForm, ReadCreditCardFields } from './forms/credit';
import { MaybeMountSwitch } from './split';
import { RenderPayPalForm } from './forms/paypal';
import { RefillPoFields, RenderPoForm } from './forms/po';
import { PromoteFormBodyIDs } from './forms/shared';
import { RenderSimpleForm } from './forms/simple';

// PromptNewPaymentOption shows the "choose payment method" picker card
// (credit/debit, PayPal, PO, check, wire).
export function PromptNewPaymentOption(state: State, $el?: HTMLElement, returnTo?: () => void): void {
    const { dom } = state;
    const $target = $el || dom.$paymentBody;
    if (!$target) {
        return;
    }

    $target.replaceChildren(template.Clone('#payment-option-form-new-payment'));
    $target.className = 'new-payment-option-select';

    const $formBody = $target.querySelector<HTMLDivElement>('.payment-option-form-body');
    if ($formBody) {
        html.IDsToClasses($formBody);
    }
    dom.$paymentBody?.querySelector<HTMLElement>('.payment-options-nav')?.remove();

    const back = returnTo ?? (() => {
        state.payment = { billingShippingSame: true };
        void state.initPayment();
    });

    setTimeout(() => {
        const $back = $target.querySelector<HTMLButtonElement>('#payment-option-back');
        if (!state.userPaymentOptions.size || !state.sess?.user.id) {
            $back?.classList.add('d-none');
        } else {
            $back?.addEventListener('click', back);
        }
    });

    mountPickerNav(state, $target, back);
}

// PromptExistingPaymentOption re-renders the in-progress (unsaved) form for
// a payment that already has type + raw data but no saved PaymentOption —
// e.g. after a failed confirm.
export function PromptExistingPaymentOption(state: State, $el?: HTMLElement, returnTo?: () => void): void {
    const p = state.payment;
    if (!p.paymentOptionTypeID || p.paymentOption) {
        return;
    }
    const uploadIDs = p.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO ?
        p.purchaseOrder?.purchaseOrderUploadIDs :
        undefined;

    PromptNewPaymentOptionByType(state, p.paymentOptionTypeID, $el, returnTo, uploadIDs);
    refillInProgressFields(p, $el);
}

function refillInProgressFields(p: Payment, $el?: HTMLElement): void {
    switch (p.paymentOptionTypeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT:
        RefillCreditFields(p, $el);
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
        RefillPoFields(p, $el);
        break;
    default:
        break;
    }
}

// PromptNewPaymentOptionByType renders the form for a specific payment
// method. Each form wires its own inputs into state.payment so submit.ts /
// CreatePaymentOption can tokenize and persist it later.
export function PromptNewPaymentOptionByType(
    state: State,
    typeID: models.PaymentOptionTypeID,
    $el?: HTMLElement,
    returnTo?: () => void,
    uploadIDs?: number[],
): void {
    const { dom } = state;
    const $target = $el || dom.$paymentBody;

    const back = returnTo ?? (() => {
        state.payment = { billingShippingSame: true };
        state.promptNewPaymentOption($target ?? undefined);
    });

    if (!$target) {
        back();
        return;
    }

    $target.classList.remove('new-payment-option-select');
    $target.classList.add('new-payment-option', `new-payment-option-${PaymentOptionTypeIDtoName(typeID, true)}`);

    state.payment.paymentOption = new models.PaymentOption();
    state.payment.paymentOptionTypeID = typeID;

    switch (typeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT:
        RenderCreditForm(state, $target, back);
        MaybeMountSwitch(state);
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK:
        // Bank Account is currently disabled in the picker (its template and
        // radio are commented out in payment-templates.html). Guard the
        // routing so a pre-existing saved bank option doesn't crash the page
        // via template.Clone of a non-existent template.
        back();
        return;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
        RenderPoForm(state, $target, back, uploadIDs);
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK:
        RenderSimpleForm(state, $target, back, '#payment-option-form-check');
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE:
        RenderSimpleForm(state, $target, back, '#payment-option-form-wire');
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
        RenderPayPalForm(state, $target, back);
        break;
    default:
        back();
        return;
    }

    mountInProgressNav($target, back);

    state.payment.paymentOptionTypeID = typeID;
    state.payment.paymentOption = undefined;
    bindConfirmHandler(state, $target);
}

// mountPickerNav adds the back button and "type confirm" button under the
// payment-method picker; clicking the chosen method swaps in the typed
// form via PromptNewPaymentOptionByType.
function mountPickerNav(state: State, $el: HTMLElement, back: () => void): void {
    $el.querySelector<HTMLElement>('.split-payment-options-nav-parent')?.remove();
    $el.appendChild(template.Clone('#payment-options-nav-new-po-select-template'));
    bindBackLink($el, back);
    $el.querySelector<HTMLButtonElement>('#payment-option-type-confirm')?.addEventListener('click', () => {
        const typeID = readSelectedTypeFromRadio($el);
        if (!typeID) {
            return;
        }
        state.payment.paymentOptionTypeID = typeID;
        state.payment.paymentOption = undefined;
        PromptNewPaymentOptionByType(state, typeID, $el);
        if (typeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL) {
            $el.querySelector<HTMLButtonElement>('#payment-option-confirm')?.dispatchEvent(new Event('click'));
        }
    });
}

// mountInProgressNav adds the back button under the typed form. The
// confirm button itself is wired by bindConfirmHandler.
function mountInProgressNav($target: HTMLElement, back: () => void): void {
    $target.appendChild(template.Clone('#payment-options-nav-new-po-template'));
    bindBackLink($target, back);
}

function bindBackLink($el: HTMLElement, back: () => void): void {
    const $back = $el.querySelector<HTMLButtonElement>('#payment-option-back');
    if (back) {
        $back?.addEventListener('click', back);
    } else {
        $back?.classList.add('d-none');
    }
}

function readSelectedTypeFromRadio($el: HTMLElement): models.PaymentOptionTypeID | undefined {
    const $selected = $el.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    if (!$selected) {
        return undefined;
    }
    switch ($selected.getAttribute('data-payment-type') || '') {
    case 'credit-debit': return models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT;
    case 'paypal': return models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL;
    case 'purchase-order': return models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO;
    case 'check': return models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK;
    case 'wire': return models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE;
    case 'bank': return models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK;
    default: return undefined;
    }
}

function bindConfirmHandler(state: State, $el: HTMLElement): void {
    const { dom } = state;
    $el.querySelector<HTMLButtonElement>('#payment-option-confirm')?.addEventListener('click', async () => {
        const p = state.payment;
        if (p.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
            const $uploader = $el.querySelector<HTMLInputElement>('.payment-option-uploader');
            $uploader?.setCustomValidity(p.uploader?.getUploads().length === 0 ? 'Please upload a file' : '');
        }
        if (!dom.$paymentBodyForm || !ReportValidity(dom.$paymentBodyForm)) {
            return;
        }
        if (dom.$paymentBodyFieldSet) {
            state.startLoading();
            dom.$paymentBodyFieldSet.disabled = true;
        }

        try {
            applyAuthorizeTextForType(state);

            if (p.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL) {
                state.updatePaymentSummary();
                completePaymentStep(state);
                // Resumed order ⇒ additional details already on the order; section hidden.
                if (state.orderResp) {
                    dom.$authorizePaymentBtn.disabled = false;
                } else {
                    dom.$additionalBodyToggle?.click();
                }
                return;
            }

            await CreatePaymentOption(state);
            p.billingShippingSame = $el.querySelector<HTMLInputElement>('.billing-shipping-same')?.checked || false;
            state.updatePaymentSummary();
            completePaymentStep(state);
            // Resumed order ⇒ additional details already on the order; section hidden.
            if (state.orderResp) {
                dom.$authorizePaymentBtn.disabled = false;
            } else {
                dom.$additionalBodyToggle?.click();
            }
        } catch (err) {
            ShowError(err);
        } finally {
            if (dom.$paymentBodyFieldSet) {
                dom.$paymentBodyFieldSet.disabled = false;
            }
            state.stopLoading();
        }
    });
}

function completePaymentStep(state: State): void {
    const { dom } = state;
    const bodyOpen = dom.$paymentBody?.classList.contains('show') || dom.$paymentBody?.classList.contains('collapsing');
    state.paymentStepComplete = true;
    dom.$paymentBodyToggle?.classList.toggle('d-none', Boolean(bodyOpen));
}

function applyAuthorizeTextForType(state: State): void {
    const { dom, payment } = state;
    [dom.$authorizePaymentText, dom.$authorizePaymentTextPlaceOrder, dom.$authorizePaymentTextPayPal].
        forEach($ => $.classList.add('d-none'));

    switch (payment.paymentOptionTypeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
        dom.$authorizePaymentTextPayPal.classList.remove('d-none');
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK:
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE:
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
        dom.$authorizePaymentTextPlaceOrder.classList.remove('d-none');
        break;
    default:
        dom.$authorizePaymentText.classList.remove('d-none');
        break;
    }
}

// EditSavedPaymentOption replaces a saved payment option with an edited
// version. Since payment options aren't mutable, we soft-delete the old and
// create a new one, with Undo on the resulting toast.
export function EditSavedPaymentOption(state: State, poID: string, returnTo?: () => void): void {
    const { dom } = state;
    const back = returnTo ?? (() => state.initPayment());

    const upo = Array.from(state.userPaymentOptions).find(u => u.id === poID);
    if (!upo?.paymentOptionTypeID || !dom.$paymentBody) {
        return;
    }

    switch (upo.paymentOptionTypeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT:
        dom.$paymentBody.replaceChildren(template.Clone('#payment-option-form-edit-cc'));
        PromoteFormBodyIDs(dom.$paymentBody);
        state.initCCInputs(dom.$paymentBody);
        MountCreditCard(state, dom.$paymentBody);
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK:
        // #payment-option-form-edit-bank is commented out while bank is
        // disabled — skip the edit flow rather than crash on Clone().
        return;
    default:
        return;
    }

    // Edit pre-fills the saved billing so the replacement card keeps it.
    const billingAddress = new BillingAddressForm(state, dom.$paymentBody);
    billingAddress.set(upo.address);

    dom.$paymentBody.querySelector<HTMLButtonElement>('#payment-option-back')?.addEventListener('click', () => back());
    dom.$paymentBody.querySelector<HTMLButtonElement>('#payment-option-confirm')?.addEventListener('click', async () => {
        if (!dom.$paymentBodyForm || !ReportValidity(dom.$paymentBodyForm)) {
            return;
        }
        await replacePaymentOption(state, upo, billingAddress).
            then(() => state.updatePaymentSummary()).
            then(back).
            catch(ShowError);
    });
}

async function replacePaymentOption(state: State, upo: models.PaymentOption, billingAddress: BillingAddressForm): Promise<void> {
    const { dom } = state;
    if (!upo?.id || !upo?.paymentOptionTypeID) {
        return;
    }
    const ccExpiry = ReadCreditCardFields(dom.$paymentBody);
    const address = billingAddress.get();

    const paymentOption: models.IPaymentOption = {
        paymentOptionTypeID: upo.paymentOptionTypeID,
        fullName: address?.fullName,
        company: address?.organization,
        country: address?.country,
        countryID: address?.countryID,
        addressLine1: address?.addressLine1,
        addressLine2: address?.addressLine2,
        city: address?.city,
        state: address?.state,
        stateID: address?.stateID,
        zip: address?.postalCode,
        email: address?.emailAddress?.email,
        phone: address?.phoneNumber?.phone,
    };

    // Tokenize the BlockChyp iframe BEFORE disabling the fieldset or deleting
    // the old option, so a bad-card failure leaves the form editable and the
    // saved option intact (PCI SAQ A — only the token + first6/last4 leave the
    // page).
    let cardTokenFields: CardTokenFields | undefined;
    let tokenMerchant: string | undefined;
    if (upo.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
        if (!state.creditCardMount) {
            throw new Error('The card form is still loading. Please re-enter your card and try again.');
        }
        ClearPlusCardFieldInvalid();
        tokenMerchant = await state.creditCardMount.webPaymentsToken.catch(() => undefined);
        try {
            const tokenized = await state.creditCardMount.tokenize({
                fullName: address?.fullName ?? '',
                month: ccExpiry.expireMonth ?? 0,
                year: ccExpiry.expireYear ?? 0,
                addressLine1: address?.addressLine1,
                addressLine2: address?.addressLine2,
                city: address?.city,
                stateCode: address?.state?.stateCode,
                zip: address?.postalCode,
                countryCode3: address?.country?.countryCode3,
                company: address?.organization,
            });
            cardTokenFields = TokenizedToFields(tokenized);
        } catch (err) {
            // Paint via the validity mirror so Plus SCSS turns the mount red —
            // rethrow so existing error handling still aborts the submit.
            if (err instanceof blockchyp.TokenizeFieldError) {
                MarkPlusCardFieldInvalid(err.field, err.message);
            }
            throw err;
        }
    }

    // A staged option has no server row to replace — overwrite the staged
    // fields with the freshly tokenized ones instead. NewPaymentOption still
    // runs at submit under the order brand (#8111).
    if (upo.id === PendingPaymentOptionID) {
        state.payment.pendingPaymentOptionCreate = {
            paymentOption,
            paymentToken: cardTokenFields?.paymentToken,
            creditCardFirst6: cardTokenFields?.creditCardFirst6,
            creditCardLast4: cardTokenFields?.creditCardLast4,
            expireMonth: ccExpiry.expireMonth,
            expireYear: ccExpiry.expireYear,
        };
        state.payment.pendingMerchantToken = tokenMerchant;
        state.payment.paymentOption = new models.PaymentOption({
            paymentOptionTypeID: paymentOption.paymentOptionTypeID,
            address,
            id: PendingPaymentOptionID,
            creditCardFirst6: cardTokenFields?.creditCardFirst6,
            creditCardLast4: cardTokenFields?.creditCardLast4,
        });
        state.creditCardMount?.unload();
        state.creditCardMount = undefined;
        return;
    }

    if (dom.$paymentBodyFieldSet) {
        dom.$paymentBodyFieldSet.disabled = true;
    }

    const deleteFunc: Promise<unknown> = state.sess?.user.id ?
        DeletePaymentOption(upo.id) :
        Promise.resolve(undefined);

    // The replace itself: soft-delete the old option, then create the new one.
    // ONLY a failure here may roll the old option back to active — once the new
    // option exists the replace has succeeded, so a later post-success hiccup
    // (toast wiring, a missing undo node) must NOT re-activate the now-deleted
    // old option.
    let resp: models.PaymentOption;
    try {
        await deleteFunc;
        resp = await NewPaymentOption({
            user: await state.getUser(),
            fattmerchantOmniWebPaymentsToken: (window as unknown as { staxOmniWebPaymentsToken: string }).staxOmniWebPaymentsToken,
            paymentOption,
            paymentToken: cardTokenFields?.paymentToken,
            creditCardFirst6: cardTokenFields?.creditCardFirst6,
            creditCardLast4: cardTokenFields?.creditCardLast4,
            expireMonth: ccExpiry.expireMonth,
            expireYear: ccExpiry.expireYear,
        });
    } catch (err) {
        if (dom.$paymentBodyFieldSet) {
            dom.$paymentBodyFieldSet.disabled = false;
        }
        if (upo.id) {
            void axios.patch(`/payment-options/${upo.id}`, { 'active': true });
        }
        state.stopLoading();
        ShowError(err);
        throw err;
    }

    if (!resp.id) {
        return;
    }

    // Adopt the new option as the selected payment. initPayment re-renders the
    // saved-card list and restoreSelection re-checks state.payment.paymentOption
    // — without this it re-checks the deleted old id, nothing is selected, and
    // the customer's previous (now top-of-list) card looks unchanged.
    state.payment.paymentOption = resp;
    if (dom.$paymentBodyFieldSet) {
        dom.$paymentBodyFieldSet.disabled = false;
    }
    state.creditCardMount?.unload();
    state.creditCardMount = undefined;

    toast.Undo('Updated payment option.', async () => {
        if (!upo.id || !resp.id) {
            return;
        }
        await undoEdit(state, upo.id, resp.id);
    });
}

async function undoEdit(state: State, oldID: string, newID: string): Promise<void> {
    await axios.patch(`/payment-options/${oldID}`, { 'active': true }).
        then(() => axios.patch(`/payment-options/${newID}`, { 'active': false })).
        then(() => state.initPayment()).
        then(() => {
            toast.Undo('Reverted payment option.', async () => {
                await redoEdit(state, oldID, newID);
            });
        }).catch(ShowError);
}

async function redoEdit(state: State, oldID: string, newID: string): Promise<void> {
    await axios.patch(`/payment-options/${oldID}`, { 'active': false }).
        then(() => axios.patch(`/payment-options/${newID}`, { 'active': true })).
        then(() => state.initPayment()).
        then(() => {
            toast.Undo('Updated payment option.', async () => {
                await undoEdit(state, oldID, newID);
            });
        }).catch(ShowError);
}
